// COP Copilot runtime — browser-safe env access (CONTRACT:C3-CAPABILITY-PLUGIN.1.0).
//
// THE NODE-ISM ADAPTATION. takbot's config.ts read `process.env.TAKBOT_*` directly (it ran in
// Node). The COP runtime runs IN THE BROWSER, where `process` is undefined and `node:fs` does
// not exist. This module is the single, defensive env seam every lifted module reads through:
//   1. Vite `import.meta.env.TAKBOT_*` (build-time-injected; the COP's primary config path), then
//   2. a GUARDED `globalThis.process?.env` (works under tsx for the check scripts + SSR), then
//   3. a runtime override map (so a test / the host can inject config without env at all).
// No `node:fs`, no bare `process` reference — safe to `vite build` AND to run under tsx.

/** A runtime override bag — lets a test or the host inject config without touching the env. */
const overrides: Record<string, string | undefined> = {};

/** Override (or clear, with undefined) an env var for the runtime — used by the check scripts /
 *  tests to drive the loop deterministically without a real environment. */
export function setEnvOverride(key: string, value: string | undefined): void {
  overrides[key] = value;
}

/** Read an env var defensively across browser (Vite) + tsx (Node) + injected overrides. Never
 *  throws and never references a bare `process` (which would ReferenceError in the browser). */
export function readEnv(key: string): string | undefined {
  if (key in overrides) return overrides[key];
  // (1) Vite build-time env (browser). import.meta.env is statically replaced by Vite.
  try {
    const viteEnv = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
    if (viteEnv && typeof viteEnv[key] === "string") return viteEnv[key];
  } catch {
    // import.meta.env unavailable in this context — fall through.
  }
  // (2) Node/tsx process.env — guarded so the browser bundle never touches a bare `process`.
  try {
    const proc = (globalThis as unknown as { process?: { env?: Record<string, string | undefined> } }).process;
    if (proc?.env && typeof proc.env[key] === "string") return proc.env[key];
  } catch {
    // no process — browser.
  }
  return undefined;
}
